import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ReactUtils from "./../react-utils";
import { geocode, getServiceArea } from "../utils/geo";
import ResourceSection from "./ResourceSection";
import ResourceListContainer from "./ResourceListContainer";

const URL_ROOT = "/search";
const DEFAULTS = {
  page: 1,
  perPage: 25,
  serviceArea: "connecticut",
  defaultLocation: "Connecticut",
};

const SurveySummaryContainer = ({ responseSet: responseSetJSON, signedIn, canClaim: canClaimProp, savedDate: savedDateProp, user: userProp, serviceLocale }) => {
  const responseSet = JSON.parse(responseSetJSON);
  const { location: responseSetLocation } = responseSet;

  const grouped = responseSet.responses.reduce((acc, response) => {
    const key = response.question_id;
    (acc[key] = acc[key] || []).push(response);
    return acc;
  }, {});
  const groupedAnswers = Object.values(grouped);
  const initialResponses = groupedAnswers
    .map((qa) => {
      const taxonomyCodes = [...new Set(
        qa.flatMap((a) =>
          a.answer.search_params && a.answer.search_params.taxonomy_code ? a.answer.search_params.taxonomy_code : []
        )
      )];
      return {
        question: qa[0].question.text,
        category: qa[0].question.category,
        order: qa[0].question.order,
        type: qa[0].question.type,
        helpUrl: qa[0].question.help_url,
        answers: qa.map((a) => a.answer.text),
        taxonomies: taxonomyCodes
          .map((taxonomyCode) => responseSet.search_params.taxonomies.find((t) => t.code === taxonomyCode))
          .filter((t) => t),
        personalInfo: qa[0].response_personal_info,
        responseId: qa[0].id,
      };
    })
    .filter((r) => r.taxonomies.length > 0)
    .sort((a, b) => a.order - b.order);

  const nameFieldInput = useRef(null);

  const [location] = useState(responseSetLocation || DEFAULTS.defaultLocation);
  const [ageRange] = useState(responseSet.search_params.age_range);
  const [referral] = useState(responseSet.search_params.referral);
  const [token] = useState(responseSet.token);
  const [canClaim, setCanClaim] = useState(canClaimProp);
  const [savedDate, setSavedDate] = useState(savedDateProp);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [responses, setResponses] = useState(initialResponses);
  const [name, setName] = useState(responseSet.name);
  const [newName, setNewName] = useState(responseSet.name);
  const [renaming, setRenaming] = useState(false);
  const [renamingError, setRenamingError] = useState(null);
  const [isModalDisplayed, setIsModalDisplayed] = useState(false);
  const [user] = useState(userProp);
  const [coords, setCoords] = useState(null);
  const [serviceArea, setServiceArea] = useState(null);

  useEffect(() => {
    geocode(location, { serviceLocale })
      .then((results) => {
        if (!results || results.length === 0) return;
        const position = results[0].geometry.location;
        const newCoords = { lng: position.lng(), lat: position.lat() };
        const area = getServiceArea(results);
        setCoords(newCoords);
        setServiceArea(area.toLowerCase());
      })
      .catch(() => {
        // Geocoding failed; map will not reposition
      });
  }, []);

  const toggleModalState = () => {
    setIsModalDisplayed((prev) => !prev);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = overflow === "hidden" ? "visible" : "hidden";
  };

  const getAllTaxonomies = () => {
    const allTaxonomies = responses.flatMap((response) => response.taxonomies);
    const seen = new Set();
    return allTaxonomies.filter((taxonomy) => {
      if (seen.has(taxonomy.id)) return false;
      seen.add(taxonomy.id);
      return true;
    });
  };

  const getCategory = (responseId) => {
    const thisResponse = responses.find((r) => r.responseId === responseId);
    return thisResponse ? thisResponse.category : null;
  };

  const onSearch = (taxonomies, responseId, resourceList) => {
    if (resourceList && resourceList.hashid) {
      sessionStorage.setItem("resource_list_id", resourceList.hashid);
    } else {
      sessionStorage.removeItem("resource_list_id");
    }

    const ids = taxonomies.map((t) => t.id);
    const queryParams = {
      page: DEFAULTS.page,
      per_page: DEFAULTS.perPage,
      location: location,
      service_area: serviceArea,
      defaultLocation: DEFAULTS.defaultLocation,
      age_range: ageRange,
      referral: referral,
      showFilters: false,
      coords: coords,
      taxonomy_code: ids,
      response_id: responseId,
      category: getCategory(responseId),
      survey_name: name,
      token: token,
    };
    window.location.href = `${URL_ROOT}?${ReactUtils.toUrlVars(queryParams)}`;
  };

  const saveResponseSet = () => {
    $.ajax(`/response_sets/${token}/claim`, {
      method: "patch",
    }).done((data) => {
      setSavedDate(data.updated_at);
      setCanClaim(false);
    });
  };

  const saveResponseInfo = () => {
    const statuses = responses.reduce(
      (acc, curr) =>
        curr.personalInfo
          ? acc.concat({
              status: curr.personalInfo.status,
              note: curr.personalInfo.note,
              id: curr.responseId,
            })
          : acc,
      []
    );
    $.ajax(`/response_sets/${token}`, {
      method: "patch",
      data: JSON.stringify({
        response_set: { name: name, responses: statuses },
      }),
      dataType: "json",
      contentType: "application/json",
    }).done((data) => {
      setSavedDate(data.updated_at);
      setHasUnsavedChanges(false);
      setRenaming(false);
    });
  };

  const removeCategory = (taxonomyCode) => {
    const newResponses = responses.map((response) => ({
      ...response,
      taxonomies: response.taxonomies.filter((taxonomy) => taxonomy.code !== taxonomyCode),
    }));
    setResponses(newResponses);
  };

  const changeProgressStatus = ({ responseId, newStatus }) => {
    const newResponses = responses.map((response) => ({
      ...response,
      personalInfo:
        response.responseId === responseId ? { ...response.personalInfo, status: newStatus } : response.personalInfo,
    }));
    setResponses(newResponses);
    setHasUnsavedChanges(true);
  };

  const getStatus = (responseId) => {
    const response = responses.find((response) => response.responseId === responseId);
    return response.personalInfo ? response.personalInfo.status : undefined;
  };

  const changeNote = ({ responseId, note }) => {
    const newResponses = responses.map((response) => ({
      ...response,
      personalInfo:
        response.responseId === responseId ? { ...response.personalInfo, note: note } : response.personalInfo,
    }));
    setResponses(newResponses);
    setHasUnsavedChanges(true);
  };

  const changeName = (e) => {
    const val = e.target.value;
    setNewName(val);
    if (val.length > 0) {
      setRenamingError(null);
    }
  };

  const onNameFieldBlur = () => {
    const trimmedName = newName.trim();
    if (trimmedName.length < 1) {
      setRenamingError("Name cannot be blank");
      nameFieldInput.current.focus();
    } else {
      setName(trimmedName);
      setNewName(trimmedName);
      setHasUnsavedChanges(true);
      setRenamingError(null);
    }
  };

  const toggleRenaming = () => {
    setRenaming(true);
  };

  const renderSaveButton = () => {
    const disabled = !hasUnsavedChanges || renamingError;
    let saveButtonStyle = "btn btn-secondary pointer";
    saveButtonStyle = disabled ? `${saveButtonStyle} disabled` : saveButtonStyle;
    if (!signedIn) {
      return (
        <a
          className="btn btn-secondary pointer save-screener"
          href={`/response_sets/${token}/deferred_claim`}
        >
          Save Responses To Your Account
        </a>
      );
    } else if (savedDate) {
      return (
        <React.Fragment>
          <a onClick={() => saveResponseInfo()} className={saveButtonStyle}>
            Save Changes
          </a>
          <div className="screener-saved">Saved on {savedDate}</div>
        </React.Fragment>
      );
    } else if (canClaim) {
      return (
        <a onClick={() => saveResponseSet()} className={saveButtonStyle}>
          Save Responses To Your Account
        </a>
      );
    }
  };

  const renderBreadcrumb = () => {
    let nameSection;

    if (renaming) {
      nameSection = (
        <input value={newName} onChange={changeName} onBlur={onNameFieldBlur} ref={nameFieldInput} />
      );
    } else {
      nameSection = <a href={`/assessments/response_sets/${responseSet.token}`}>{name}</a>;
    }

    return (
      <div>
        <ol className="breadcrumb top">
          <li>
            <a href="/my211/screeners">Screeners</a>
          </li>
          <li className="active">{nameSection}</li>
          {!renaming && (
            <li className="no-arrow">
              <a href="#" className="rename-screener-link" onClick={toggleRenaming}>
                Rename
              </a>
            </li>
          )}
        </ol>
        {renamingError && <div className="renaming-error">{renamingError}</div>}
      </div>
    );
  };

  const csrfParam = $('meta[name="csrf-param"]').attr("content");
  const csrfToken = $('meta[name="csrf-token"]').attr("content");
  const allTaxonomies = getAllTaxonomies();
  const surveyFormHeaderClassName = signedIn ? "survey-form-header" : "survey-form-header right";

  return (
    <div className="row survey-container">
      <div className="survey-content">
        <div className="summary">
          <div>
            <div className="survey-form">
              <div className={surveyFormHeaderClassName}>
                {signedIn && renderBreadcrumb()}
                <div className="save-screener top">{renderSaveButton()}</div>
              </div>
              <p>Thank you for completing the screener!</p>
              <p>
                Based on your responses, we think you may benefit from these resources. Remove search terms that don't
                meet your needs by clicking on the X. Add resources to any specific topic using the green Add
                Resources button. If you are logged into your My 211 Account, you will have the option to mark the
                status of a resource as well as to type in reminder notes. Use the PDF option on the top right corner
                to download a summary.
              </p>

              <form className="pdf-download-row" action="/response_sets/export.pdf" method="post">
                <input type="hidden" name="token" value={token} />
                <input type="hidden" name={csrfParam} value={csrfToken} />
                <input type="submit" value="Download PDF of this Summary" className="summary-pdf-download" />
              </form>
              <hr className="category-separator" />
              <div className="answers">
                {responses.map((questionAnswer, index) => (
                  <ResourceSection
                    category={questionAnswer.category}
                    key={index}
                    status={getStatus(questionAnswer.responseId)}
                    changeProgressStatus={changeProgressStatus}
                    hasTaxonomies={questionAnswer.taxonomies.length > 0}
                    signedIn={signedIn}
                    responseId={questionAnswer.responseId}
                  >
                    <ResourceListContainer
                      key={index}
                      questionAnswer={questionAnswer}
                      removeCategory={removeCategory}
                      onSearch={onSearch}
                      signedIn={signedIn}
                      changeNote={changeNote}
                      toggleModalState={toggleModalState}
                      isModalDisplayed={isModalDisplayed}
                      user={user}
                      surveyName={name}
                      category={questionAnswer.category}
                      token={token}
                    />
                  </ResourceSection>
                ))}
                <a onClick={() => onSearch(allTaxonomies)} className="btn btn-default pointer search-all">
                  Search for all categories ({allTaxonomies.length})
                </a>
                <div className="save-screener">{renderSaveButton()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isModalDisplayed && <div className="modal-backdrop fade in"></div>}
    </div>
  );
};

SurveySummaryContainer.propTypes = {
  responseSet: PropTypes.string,
  signedIn: PropTypes.bool,
  canClaim: PropTypes.bool,
  savedDate: PropTypes.string,
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  serviceLocale: PropTypes.shape({
    abbr: PropTypes.string,
    stateName: PropTypes.string,
  }),
};

export default SurveySummaryContainer;
